import { useMemo } from "react";
import { INameValue, EAgencies } from "src/shared/types/common.types";
import {
  EOriginalAvailability,
  EContractState,
  ETaxSystem,
} from "src/shared/types/partner.types";
import {
  useGetpartnerComplexContractsListQuery,
  useGetpartnerVirtualContractsListQuery,
} from "../api/api";
import {
  IPartnerComplexContractItem,
  IContractAdditionalFields,
  EContractType,
} from "../types";

// TODO: временный workaround
// Сейчас INameValue<T> не допускает null, но в ряде кейсов (например, при отсутствии complexContracts)
// у нас нет валидного значения для agency/tax_system/original_availability.
// Используем приведение через unknown, чтобы избежать падения.
//
// В будущем:
// - добавить "Unknown" значения в enum'ы или
// - разрешить null в INameValue<T>

const defaultAgency: INameValue<EAgencies> = {
  name: "",
  value: null as unknown as EAgencies,
};
const defaultTaxSystem: INameValue<ETaxSystem> = {
  name: "",
  value: null as unknown as ETaxSystem,
};
const defaultAvailability: INameValue<EOriginalAvailability> = {
  name: "",
  value: null as unknown as EOriginalAvailability,
};

export function useGetAllContractsList({ partner_id }: { partner_id: string }) {
  const { data: complexContracts, isFetching: isFetchingComplex } =
    useGetpartnerComplexContractsListQuery({
      partner_id,
    });

  const { data: virtualContracts, isFetching: isFetchingVirtual } =
    useGetpartnerVirtualContractsListQuery({
      partner_id,
    });

  const mappedComplexContracts: Array<
    IPartnerComplexContractItem & IContractAdditionalFields
  > = useMemo(() => {
    return (
      complexContracts?.data.reduce(
        (acc, curr) => {
          let mappedAgreements: Array<
            IPartnerComplexContractItem & IContractAdditionalFields
          > = [];
          if (curr.agreements.length) {
            mappedAgreements = curr.agreements.map((agreement) => {
              return {
                ...agreement,
                agreements: [],
                type: EContractType.agreement,
                site: "",
                original_availability: agreement.is_original_presented
                  ? { name: "В наличии", value: EOriginalAvailability.IN_STOCK }
                  : {
                      name: "Отсутствует",
                      value: EOriginalAvailability.ABSENT,
                    },
                contract_date: agreement.agreement_date,
                partner_id: curr.partner_id,
                agency: complexContracts?.data?.[0]?.agency ?? defaultAgency,
                contract_number: `${agreement.id}-${curr.contract_number}`,
                contract_state: agreement.is_active
                  ? { name: "Действует", value: EContractState._2 }
                  : { name: "Не активно", value: EContractState._0 },
                contract_email: null,
                currency: curr.currency,
                tax_value: 0,
                partner_bonus_min_sum: 0,
                tax_system:
                  complexContracts?.data?.[0]?.tax_system && defaultTaxSystem,
                has_tax_certificate: false,
                tax_certificate_path: null,
                note: agreement.note! || "",
                contract_manager: null,
                context_teamlead: null,
                account_manager: null,
                contract_creator: null,
                with_edits: false,
                edit_notes: "",
                contract_original_file_path: "",
                customer_info: {
                  id: 0,
                  represented_by: "",
                  signs: "",
                  bases_on: "",
                  position: "",
                },
                executor_info: {
                  id: 0,
                  represented_by: null,
                  signs: null,
                  bases_on: null,
                  position: null,
                },
                is_EDMS_enabled: false,
                is_nda_signed: false,
              };
            });
          }

          return [
            ...acc,
            { ...curr, type: EContractType.complex },
            ...mappedAgreements,
          ];
        },

        [] as IPartnerComplexContractItem[],
      ) || []
    );
  }, [complexContracts?.data]);

  const mappedVirtualContracts: Array<
    IPartnerComplexContractItem & IContractAdditionalFields
  > = useMemo(() => {
    return (
      virtualContracts?.data.map((virtualContract) => {
        return {
          ...virtualContract,
          agreements: [],
          type: EContractType.virtual,
          agency: complexContracts?.data?.[0]?.agency ?? defaultAgency,
          contract_number: virtualContract.contract_number,
          contract_state: virtualContract.state,
          contract_email: null,
          currency: virtualContract.service_currency,
          tax_value: 0,
          partner_bonus_min_sum: 0,
          tax_system:
            complexContracts?.data?.[0]?.tax_system ?? defaultTaxSystem,
          has_tax_certificate: false,
          tax_certificate_path: null,
          original_availability:
            complexContracts?.data?.[0]?.original_availability ??
            defaultAvailability,
          contract_manager: null,
          context_teamlead: null,
          account_manager: null,
          contract_creator: null,
          with_edits: false,
          edit_notes: "",
          contract_original_file_path: "",
          customer_info: {
            id: 0,
            represented_by: "",
            signs: "",
            bases_on: "",
            position: "",
          },
          executor_info: {
            id: 0,
            represented_by: null,
            signs: null,
            bases_on: null,
            position: null,
          },
          is_EDMS_enabled: false,
          is_nda_signed: false,
        };
      }) || []
    );
  }, [virtualContracts, complexContracts]);

  return {
    rows: [...mappedComplexContracts, ...mappedVirtualContracts],
    isFetching: isFetchingComplex || isFetchingVirtual,
  };
}
